import React from 'react';
import { Formik } from 'formik';

import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import { getPopoverTriggerAria } from '~/components/ui/atoms/Popover/usePopoverState';

import CardDialog from '../../CardDialog';
import { ShowOnlyTextField } from '../../TextFieldFormik';

import HolidaySubdivisionSelectFormik, { NO_HOLIDAY_SUBDIVISION } from './HolidaySubdivisionSelectFormik';
import type { HolidaySubdivision, OnUpdateOrganizationSettings, SettingsOrganization } from './types';
import { useHolidaySubdivisions } from './useHolidaySubdivisions';

import { useStyles } from '../../../assets/styles';

const subdivisionLabel = (subdivisions: HolidaySubdivision[], code: string): string =>
  subdivisions.find((subdivision) => subdivision.code === code)?.name || code;

interface HolidaySubdivisionSettingProps {
  organization: SettingsOrganization;
  onUpdateSettings: OnUpdateOrganizationSettings;
}

function HolidaySubdivisionSetting({
  organization,
  onUpdateSettings,
}: HolidaySubdivisionSettingProps): React.ReactElement {
  const [popoverAnchorEl, setPopoverAnchorEl] = React.useState<HTMLElement | undefined>(undefined);
  const popoverId = React.useId();
  const { classes } = useStyles();
  // No locale override: this setting edits the subdivision under the organization's stored locale.
  const { territory, subdivisions, isLoading, isError } = useHolidaySubdivisions(organization.id);

  return (
    <>
      <ShowOnlyTextField
        classes={classes}
        label="Holiday Subdivision"
        showOnlyValueComponent={
          organization.holiday_subdiv ? subdivisionLabel(subdivisions, organization.holiday_subdiv) : '—'
        }
        onEdit={(e: React.MouseEvent<HTMLElement>) => setPopoverAnchorEl(e.currentTarget)}
        editTriggerAria={getPopoverTriggerAria(Boolean(popoverAnchorEl), popoverId)}
      />

      <CardDialog
        isPopover
        open={Boolean(popoverAnchorEl)}
        anchorEl={popoverAnchorEl}
        id={popoverId}
        title="Holiday Subdivision"
        onClose={() => setPopoverAnchorEl(undefined)}
      >
        <Formik
          initialValues={{ holiday_subdiv: organization.holiday_subdiv || NO_HOLIDAY_SUBDIVISION }}
          enableReinitialize
          onSubmit={async (values, formikProps) => {
            try {
              await onUpdateSettings({ holiday_subdiv: values.holiday_subdiv || null });
              setPopoverAnchorEl(undefined);
            } catch {
              formikProps.setSubmitting(false);
            }
          }}
        >
          {(formikProps) => (
            <>
              <HolidaySubdivisionSelectFormik
                id="holiday_subdiv"
                territory={territory}
                subdivisions={subdivisions}
                isLoading={isLoading}
                isError={isError}
              />
              <div className={classes.buttonsContainer}>
                <CancelButton
                  disabled={formikProps.isSubmitting}
                  onClick={() => {
                    formikProps.handleReset();
                    setPopoverAnchorEl(undefined);
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  disabled={formikProps.isSubmitting}
                  onClick={() => formikProps.handleSubmit()}
                >
                  Save
                </Button>
              </div>
            </>
          )}
        </Formik>
      </CardDialog>
    </>
  );
}

export default HolidaySubdivisionSetting;
